import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  ChevronLeft, 
  ChevronRight, 
  Filter,
  Calendar,
  DollarSign,
  Zap,
  CreditCard,
  RefreshCw,
  Download
} from 'lucide-react';
import { useBillingHistory } from '@/hooks/useBilling';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface BillingHistoryTabProps {
  className?: string;
}

export default function BillingHistoryTab({ className }: BillingHistoryTabProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string } | undefined>();

  // Get billing history with pagination
  const { 
    data: billingData, 
    isLoading, 
    error,
    refetch 
  } = useBillingHistory(page, pageSize, dateRange);

  const handleDateRangeChange = (range: string) => {
    const now = new Date();
    let startDate: Date;

    switch (range) {
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1y':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'all':
        setDateRange(undefined);
        return;
      default:
        return;
    }

    setDateRange({
      startDate: startDate.toISOString(),
      endDate: now.toISOString(),
    });
    setPage(1); // Reset to first page when changing date range
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Scroll to top of the history section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const downloadBillingHistory = () => {
    if (!billingData?.history || billingData.history.length === 0) {
      return;
    }

    // Convert billing history to CSV format (accounting-friendly)
    const headers = [
      'Date',
      'Time',
      'Transaction Type',
      'Description', 
      'Amount (USD)',
      'Credits',
      'Status',
      'Reference ID',
      'Transaction ID'
    ];

    const csvData = billingData.history.map((transaction: {
      id: string;
      type: string;
      date: string;
      description: string;
      amount: number;
      credits: number;
      status: string;
      stripe_charge_id?: string;
    }) => {
      const transactionDate = new Date(transaction.date);
      const typeFormatted = transaction.type
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      
      return [
        format(transactionDate, 'yyyy-MM-dd'),
        format(transactionDate, 'HH:mm:ss'),
        typeFormatted,
        `"${transaction.description.replace(/"/g, '""')}"`, // Escape quotes properly
        transaction.amount > 0 ? transaction.amount.toFixed(2) : '0.00',
        transaction.credits !== 0 ? transaction.credits.toFixed(1) : '0.0',
        transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1),
        transaction.stripe_charge_id || '',
        transaction.id
      ];
    });

    // Add header row with metadata
    const dateRangeText = dateRange ? 
      `${format(new Date(dateRange.startDate), 'yyyy-MM-dd')} to ${format(new Date(dateRange.endDate), 'yyyy-MM-dd')}` : 
      'All time';
    
    const csvContent = [
      [`# Billing History Export - ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`],
      [`# Date Range: ${dateRangeText}`],
      [`# Total Records: ${billingData.history.length}`],
      [''], // Empty row
      headers,
      ...csvData
    ].map(row => row.join(',')).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const filename = dateRange ? 
      `billing-history-${format(new Date(dateRange.startDate), 'yyyy-MM-dd')}-to-${format(new Date(dateRange.endDate), 'yyyy-MM-dd')}.csv` :
      `billing-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
    
    // Show success notification
    toast.success(`Downloaded ${billingData.history.length} transactions to ${filename}`);
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'boost_pack':
      case 'boost_pack_purchase':
        return <Zap className="h-4 w-4" />;
      case 'subscription_credit':
      case 'subscription_grant':
        return <CreditCard className="h-4 w-4" />;
      case 'subscription_payment':
        return <DollarSign className="h-4 w-4" />;
      case 'auto_refill':
        return <RefreshCw className="h-4 w-4" />;
      case 'credit_expiration':
      case 'manual_adjustment':
        return <Calendar className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    switch (type) {
      case 'boost_pack':
      case 'boost_pack_purchase':
        return <Badge variant="default">Purchase</Badge>;
      case 'subscription_credit':
      case 'subscription_grant':
        return <Badge variant="secondary">Grant</Badge>;
      case 'subscription_payment':
        return <Badge variant="default">Payment</Badge>;
      case 'auto_refill':
        return <Badge variant="outline">Auto-Refill</Badge>;
      case 'credit_expiration':
      case 'manual_adjustment':
        return <Badge variant="destructive">Expired</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-600">
            <p>Failed to load billing history</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-2">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className}>
      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="date-range">Date Range</Label>
              <Select onValueChange={handleDateRangeChange} defaultValue="30d">
                <SelectTrigger>
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="1y">Last year</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <Label htmlFor="page-size">Items per page</Label>
              <Select 
                value={pageSize.toString()} 
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Billing History</CardTitle>
              <CardDescription>
                Your credit transactions and billing activity. Download for your records and bookkeeping.
              </CardDescription>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={downloadBillingHistory}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    disabled={!billingData?.history || billingData.history.length === 0}
                  >
                    <Download className="h-4 w-4" />
                    Download CSV
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download current page transactions as CSV for bookkeeping</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-4" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-3 w-[150px]" />
                  </div>
                  <Skeleton className="h-4 w-[80px]" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {billingData?.history && billingData.history.length > 0 ? (
                <div className="space-y-4">
                  {billingData.history.map((transaction: {
                    id: string;
                    type: string;
                    date: string;
                    description: string;
                    amount: number;
                    credits: number;
                    status: string;
                    stripe_charge_id?: string;
                  }) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getTransactionIcon(transaction.type)}
                          <div>
                            <p className="font-medium">{transaction.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(transaction.date), 'MMM dd, yyyy HH:mm')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="font-medium">
                            {transaction.credits > 0 ? '+' : ''}{transaction.credits.toFixed(1)} credits
                          </p>
                          {transaction.amount > 0 && (
                            <p className="text-sm text-muted-foreground">
                              ${transaction.amount.toFixed(2)}
                            </p>
                          )}
                        </div>
                        {getTransactionBadge(transaction.type)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No transactions found</p>
                  {dateRange && (
                    <Button 
                      variant="outline" 
                      onClick={() => setDateRange(undefined)}
                      className="mt-2"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {billingData?.pagination && billingData.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    {/* FIXED: Clear display of filtered results vs server total */}
                    {billingData.pagination.actualResults > 0 ? (
                      <>
                        Showing {billingData.pagination.displayStart} to{' '}
                        {billingData.pagination.displayEnd} of{' '}
                        {billingData.pagination.total} total records
                        {billingData.pagination.isFiltered && (
                          <span className="text-xs text-muted-foreground ml-2">
                            (filtered view - {billingData.pagination.actualResults} relevant on this page)
                          </span>
                        )}
                      </>
                    ) : (
                      `No transactions found (${billingData.pagination.total} total records available)`
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={!billingData.pagination.hasPrevPage}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, billingData.pagination.totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="w-8 h-8"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={!billingData.pagination.hasNextPage}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 